import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { InsightsService } from '../../../services/insights-service';
import { interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnSubscribeComponent } from '../../_shared/un-subscribe.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardComponent extends UnSubscribeComponent implements OnInit, AfterViewInit {
  @ViewChild('reportContainer', { static: false }) reportContainer;
  private MINUTES_BEFORE_EXPIRATION: number = 10;
  private INTERVAL_TIME = 60000;
  private tokenExpiration: string;
  private dashboardId: number;
  private visibilityChangeHandler: () => void;
  hasError: boolean;
  errorMsg: string;
  isLoading: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private insightsService: InsightsService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    this.activatedRoute.params
      .pipe(takeUntil(this.unSubscribeOnDestroy))
      .subscribe(params => {
        this.dashboardId = +params['id'];
      });
  }

  ngAfterViewInit() {
    this.activatedRoute.params
      .pipe(takeUntil(this.unSubscribeOnDestroy))
      .subscribe(params => {
        this.dashboardId = +params['id'];
        this.loadDashboard();
      });
  }

  private loadDashboard() {
    this.isLoading = true;
    this.hasError = false;
    this.cdr.detectChanges();

    const powerbi = window['powerbi'];
    const reportContainer = this.reportContainer.nativeElement;
    powerbi.reset(reportContainer);

    this.insightsService.getEmbedData(true, this.dashboardId).subscribe((embedData) => {
      this.isLoading = false;
      if (embedData.status === 200) {
        const models = window['powerbi-client'].models;
        const reportLoadConfig = {
          type: 'report',
          tokenType: models.TokenType.Embed,
          accessToken: embedData.accessToken,

          // Use other embed report config based on the requirement. We have used the first one for demo purpose
          embedUrl: embedData.embedUrl[0].embedUrl,

          // Enable this setting to remove gray shoulders from embedded report
          // settings: {
          //     background: models.BackgroundType.Transparent
          // }
        };

        // Use the token expiry to regenerate Embed token for seamless end user experience
        // Refer https://aka.ms/RefreshEmbedToken
        this.handleRefreshToken(embedData);

        // Embed Power BI report when Access token and Embed URL are available
        let report = powerbi.embed(reportContainer, reportLoadConfig);

        // Clear any other loaded handler events
        report.off('loaded');

        // Triggers when a report schema is successfully loaded
        report.on('loaded', function() {
          console.log('Report load successful');
        });

        // Clear any other rendered handler events
        report.off('rendered');

        // Triggers when a report is successfully embedded in UI
        report.on('rendered', function() {
          console.log('Report render successful');
        });

        // Clear any other error handler events
        report.off('error');

        // Handle embed errors
        report.on('error', function(event) {
          let errorMsg = event.detail;
          console.error(errorMsg);
          return;
        });
      } else {
        this.hasError = true;
        const newLineIndex = embedData.error.indexOf('\r\n');
        this.errorMsg = newLineIndex !== -1 ? embedData.error.substring(0, newLineIndex) : embedData.error;
        console.log(embedData.error);
      }
    });
  }

  private checkTokenAndUpdate() {
    // Get the current time
    const currentTime = Date.now();
    const expirationDate = Date.parse(this.tokenExpiration);

    // Time until token expiration in milliseconds
    const timeUntilExpiration = expirationDate - currentTime;
    const timeToUpdate = this.MINUTES_BEFORE_EXPIRATION * 60 * 1000;

    // // Update the token if it is about to expired
    if (timeUntilExpiration <= timeToUpdate) {
      console.log('Updating report access token');
      this.updateToken();
    }
  }

  private updateToken() {
    const powerbi = window['powerbi'];
    // Get a reference to the embedded report HTML element
    let embedContainer = document.getElementById('reportContainer');

    if (!embedContainer) {
      return;
    }

    // Guard against calling get() on a container that has been reset
    if (!powerbi.find(embedContainer)) {
      return;
    }

    // Get a reference to the embedded report.
    let report = powerbi.get(embedContainer);

    // Generate a new embed token or refresh the user Azure AD access token
    this.insightsService.getEmbedData(false, this.dashboardId).subscribe(newEmbedData => {
      // Update the new token expiration time
      this.tokenExpiration = newEmbedData.expiry;

      // Set the new access token
      report.setAccessToken(newEmbedData.accessToken);
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }

  private handleRefreshToken(embedData) {
    this.tokenExpiration = embedData.expiry;
    interval(this.INTERVAL_TIME)
      .pipe(
        takeUntil(this.unSubscribeOnDestroy),
      ).subscribe(() => {
      this.checkTokenAndUpdate();
    });

    // Remove previous listener before adding a new one to avoid stale handlers
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    this.visibilityChangeHandler = () => {
      if (!document.hidden) {
        this.updateToken();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }
}
